import { useState } from 'react';

import { burnAuctionAbi, erc20Abi, hypeBuybackAbi, hypeConverterAbi } from '../../../offchain/src/abi.ts';
import { clearBook, NO_TICK, TICK_PRICES, TICKS, tickAtOrBelow } from '../../../offchain/src/auction.ts';
import { Figure, Hint, Line, Question } from '../components/Explain.tsx';
import { compactUsd, copyText } from '../components/ui.tsx';
import { deployment } from '../config.ts';
import { amountInput, fmtUnits, fmtUsd, parseUnits, pct, rewardPhase, shortAddr, toInput, WAD } from '../lib/format.ts';
import { deadlineIn, newNonce, run } from '../lib/intents.ts';
import { quotePapurr, type Globals } from '../lib/protocol.ts';
import { errorMessage, sendTx, walletReader } from '../lib/tx.ts';
import { useApp } from '../state.tsx';

const dash = '- -';
/** HYPE has 8 decimals on HyperCore */
const HYPE_DECIMALS = 8;
const fmtHype = (wei: bigint) => fmtUnits(wei, HYPE_DECIMALS, wei >= 100n * 10n ** 8n ? 2 : 4);
const fmtPct = (v: number) => `${v.toFixed(v >= 10 ? 0 : v >= 1 ? 1 : 2)}%`;
const ceilDiv = (a: bigint, b: bigint) => (a + b - 1n) / b;
const fmtLeft = (sec: number) => {
  const d = Math.floor(sec / 86_400);
  const h = Math.floor((sec % 86_400) / 3_600);
  const m = Math.floor((sec % 3_600) / 60);
  return d > 0 ? `${d}d ${h}h` : h > 0 ? `${h}h ${m}m` : `${Math.max(1, m)}m`;
};

/** What stakers earn right now, in plain words, by the house's state (PapurrExchange reward tiers) */
const PHASE = {
  queued: { cls: 'warn', text: 'Fees only', detail: 'The house owes more than it holds: the share of losses pauses until it is back above zero.' },
  losses: { cls: 'pos', text: 'Earning on every trade', detail: 'Stakers get a share of every loss and half of every win fee.' },
  belowRecord: { cls: 'pos', text: 'Earning on every trade', detail: 'The house is above its threshold but under its record: stakers share its gains again once it passes it.' },
  newHigh: { cls: 'pos', text: 'Earning on trades and house gains', detail: 'The house is at a record: stakers also share what it gains past it.' },
} as const;

/** The boost of the curve's rate under the threshold now, e.g. "×1.75"; undefined on a tokenomics without the ramp */
function boostNow(g: Globals): string | undefined {
  const m = g.mint.lowHouseMultiple;
  if (m === undefined) return undefined;
  const deficit = g.mint.flatThreshold - g.effectiveLp;
  const frac = deficit >= g.mint.flatThreshold ? 1 : Number(deficit) / Number(g.mint.flatThreshold);
  return `×${(1 + Math.max(0, Number(m) / 1e18 - 1) * frac).toFixed(2)}`;
}

const NO_CORE = 'Your address has no HyperCore account: claim to this wallet instead, or deposit on Hyperliquid once.';
/** A hundredth of a HYPE, 18 dec: the lot of the HYPE/USDC pair, what conversions are counted in */
const HYPE_LOT = 10n ** 16n;
/** Native HYPE left in the wallet for gas when converting everything */
const GAS_RESERVE = 2n * 10n ** 16n;

/**
 * PAPURR in three steps: the hero says how it is earned and what you hold and can claim (one claim for the
 * staking stream and the auction together); then one card per thing to do with it (stake, sell to the burn
 * auction, buy back from it), and two cards on where it comes from and what it is.
 */
export function PapurrPage() {
  const { user, globals, markets, buyback, auction, converter, wallet, signer, toast, refresh, openConnect } = useApp();
  // HYPE in USD at the HYPE market's mid (the one market), for a rough value of the rewards
  const hype = markets?.[0];
  const hypeUsd = (wei: bigint) =>
    hype?.mid !== undefined ? (wei * hype.mid * 10n ** 10n) / BigInt(hype.inst.priceScale) : undefined;
  const hasAuction = !!deployment.burnAuction;
  // the auction is off while the buyback sets nothing aside for it (at launch); its cards stay while it still
  // holds an epoch's offers or PAPURR on sale, so nobody is left without a way to reprice or buy
  const auctionOn = hasAuction && !!buyback && buyback.auctionBps !== 0;
  const showAuction = auctionOn || (hasAuction && !!auction && (auction.soldWei > 0n || auction.saleAvailableWei > 0n));
  const auctionShare = buyback?.auctionBps ? fmtPct(buyback.auctionBps / 100) : 'a share';
  const epochDays = auction ? Math.round(auction.durationSec / 86_400) : undefined;
  const hasConverter = !!deployment.hypeConverter;

  // --- what you hold and can claim
  const held = user ? user.papurr + user.staked : 0n;
  const poolShare = user && globals && globals.totalStaked > 0n ? Number((user.staked * 1_000_000n) / globals.totalStaked) / 10_000 : undefined;
  const claimable = user ? user.pendingReward + (hasAuction ? user.auctionClaimable : 0n) : 0n;
  const claimableUsd = user ? hypeUsd(claimable) : undefined;
  // PapurrStaking.INACTIVITY_PERIOD: after 100 days without a stake, unstake or claim, anyone can sweep the
  // unclaimed HYPE of the stream to the dev wallet
  const sweepDaysLeft =
    user && user.pendingReward > 0n ? Math.ceil((user.stakingLastActive + 100 * 86_400 - Date.now() / 1000) / 86_400) : undefined;
  const returnable = user && hasAuction ? user.auctionReturnable : 0n;
  const claimSub = user
    ? [
        claimableUsd !== undefined && claimable > 0n ? `≈ $${fmtUsd(claimableUsd)}` : undefined,
        hasAuction && user.pendingReward > 0n && user.auctionClaimable > 0n
          ? `${fmtHype(user.pendingReward)} from staking · ${fmtHype(user.auctionClaimable)} from the auction`
          : hasAuction && user.auctionClaimable > 0n
            ? 'from the auction'
            : undefined,
        returnable > 0n ? `${fmtUnits(returnable, 18, 0)} PAPURR to take back (left out of an auction epoch)` : undefined,
        sweepDaysLeft === undefined ? undefined : sweepDaysLeft > 0 ? `claim within ${sweepDaysLeft} days` : 'idle 100 days: can be swept',
      ]
        .filter(Boolean)
        .join(' · ')
    : undefined;

  // --- the stream
  const days = buyback ? Number(buyback.durationSec) / 86_400 : 7;
  const streamDays = `${days % 1 === 0 ? days : days.toFixed(1)} day${days === 1 ? '' : 's'}`;
  const mins = buyback ? Number(buyback.buyIntervalSec) / 60 : 0;
  const pace =
    buyback && buyback.maxBuyWei > 0n
      ? `in orders of at most $${fmtUnits(buyback.maxBuyWei, 8, 0)} every ${mins % 1 === 0 ? mins : mins.toFixed(1)} minute${mins === 1 ? '' : 's'}`
      : 'in small orders';
  const phase = globals ? PHASE[rewardPhase(globals.effectiveLp, globals.rewardThreshold, globals.peakExcess)] : undefined;

  // --- the mint
  const ratePerUsd = globals ? quotePapurr(WAD, globals) : undefined;
  const flatLeft = globals ? globals.mint.flatThreshold - globals.effectiveLp : undefined;
  const ratio = globals && globals.papurrSupply > 0n ? Number((globals.totalStaked * 1000n) / globals.papurrSupply) / 10 : undefined;

  // --- the auction: the running epoch (where sales go), and the previous one (what is on sale)
  const epochEndsIn = auction ? auction.startSec + auction.durationSec - Date.now() / 1000 : undefined;
  const endsText = epochEndsIn === undefined ? dash : epochEndsIn > 0 ? `in ${fmtLeft(epochEndsIn)}` : 'now, at the next action';
  const potUsd = auction ? hypeUsd(auction.potWei) : undefined;
  const lastPotUsd = auction ? hypeUsd(auction.lastPotWei) : undefined;
  const lastPerK = auction && lastPotUsd !== undefined && auction.lastClearedWei > 0n ? (lastPotUsd * 1_000n * WAD) / auction.lastClearedWei : undefined;
  // prices on the auction's grid are HYPE core wei per whole PAPURR; shown per 1,000 PAPURR
  const perK = (price: bigint) => {
    const usd = hypeUsd(price * 1_000n);
    return usd !== undefined ? `≈ $${fmtUsd(usd)} per 1,000 PAPURR` : `${fmtHype(price * 1_000n)} HYPE per 1,000 PAPURR`;
  };
  const tickLabel = (t: number) => (t === 0 ? 'any price' : perK(TICK_PRICES[t]!));
  const previewText =
    auction && auction.previewClearedWei > 0n
      ? `would clear now at ${perK(auction.previewPrice)}: ${fmtUnits(auction.previewClearedWei, 18, 0)} of it taken`
      : auction && auction.soldWei === 0n
        ? 'nobody yet: the first offer sets the price'
        : auction
          ? 'nothing would be taken yet: every reserve is above what the pot pays'
          : undefined;
  // buying back: the sale epoch's price (its pot over what was sold into it) plus the premium and the fee, as
  // PapurrBurnAuction._price rounds it; the transaction itself asks the contract for the exact amount
  const markBps = auction ? 10_000n + BigInt(auction.premiumBps) + BigInt(auction.feeBps) : 11_000n;
  const markPct = auction ? fmtPct((auction.premiumBps + auction.feeBps) / 100) : '11%';
  const feePct = auction ? fmtPct(auction.feeBps / 100) : '1%';
  const buyMax = auction ? auction.saleAvailableWei : 0n;
  const salePerK =
    auction && auction.saleClearedWei > 0n ? hypeUsd(ceilDiv(1_000n * WAD * auction.salePotWei * markBps, auction.saleClearedWei * 10_000n)) : undefined;

  // --- forms
  const [mode, setMode] = useState<'stake' | 'unstake'>('stake');
  const [text, setText] = useState('');
  const [sellText, setSellText] = useState('');
  const [buyText, setBuyText] = useState('');
  const [busy, setBusy] = useState<string>();
  // where a claim is paid: HyperCore unless the address has no account there (a Safe, for one)
  const [claimTo, setClaimTo] = useState<'core' | 'evm'>();
  const dest = claimTo ?? (user && !user.coreUser ? 'evm' : 'core');
  const evmInFlight = user ? user.evmQueued + user.evmOwed : 0n;
  // --- conversions: whole hundredths of a HYPE, at least the contract's minimum, from the wallet's native HYPE
  const [convertText, setConvertText] = useState('');
  const convertAmount = parseUnits(convertText, 18);
  const convertMin = converter ? converter.minConvertWei * 10n ** 10n : 3n * 10n ** 17n;
  const convertMax = user && user.gas > GAS_RESERVE ? ((user.gas - GAS_RESERVE) / HYPE_LOT) * HYPE_LOT : 0n;
  const convertOk = !!convertAmount && convertAmount % HYPE_LOT === 0n && convertAmount >= convertMin && convertAmount <= convertMax;
  const convertEst = convertAmount && converter && converter.askPx > 0n ? (convertAmount * converter.askPx) / 10n ** 8n : undefined;
  const amount = parseUnits(text, 18);
  const max = user ? (mode === 'stake' ? user.papurr : user.staked) : 0n;
  // the minimum stake: what the account holds staked after the action must be 0 or at least it
  const minStake = globals?.minStake ?? 0n;
  const stakedAfter = user && amount ? (mode === 'stake' ? user.staked + amount : user.staked - amount) : undefined;
  const underMin = minStake > 0n && stakedAfter !== undefined && stakedAfter > 0n && stakedAfter < minStake;
  const sellAmount = parseUnits(sellText, 18);
  const sellMax = user ? user.papurr : 0n;
  // --- the reserve: the lowest price the seller accepts, a tick of the auction's grid. The default sits a fifth
  // under the last price paid, else under today's clearing, else under the pot over what is offered; with no
  // reference at all it is "any price". An offer already in the epoch starts from its own reserve.
  const [reserveTick, setReserveTick] = useState<number>();
  const refPrice =
    auction && auction.lastClearedWei > 0n
      ? (auction.lastPotWei * WAD) / auction.lastClearedWei
      : auction && auction.previewPrice > 0n
        ? auction.previewPrice
        : auction && auction.soldWei > 0n
          ? (auction.potWei * WAD) / auction.soldWei
          : undefined;
  const defaultTick = user && user.auctionSold > 0n ? user.auctionTick : refPrice !== undefined ? tickAtOrBelow((refPrice * 4n) / 5n) : 0;
  const tick = reserveTick ?? defaultTick;
  const repricing = !!user && user.auctionSold > 0n && tick !== user.auctionTick;
  // the epoch as it would close now with this offer in it (an existing offer moves with its reserve)
  const sim = (() => {
    if (!auction || !user) return undefined;
    const book = Array.from({ length: TICKS }, (_, k) => auction.book[k] ?? 0n);
    if (user.auctionSold > 0n) {
      book[user.auctionTick] = book[user.auctionTick]! - user.auctionSold;
      book[tick] = book[tick]! + user.auctionSold;
    }
    book[tick] = book[tick]! + (sellAmount ?? 0n);
    return clearBook(book, auction.potWei);
  })();
  const simTaken = !!sim && sim.cutoff !== NO_TICK && tick <= sim.cutoff;
  const yourTaken = !!auction && !!user && user.auctionSold > 0n && auction.previewCutoff !== NO_TICK && user.auctionTick <= auction.previewCutoff;
  const buyAmount = parseUnits(buyText, 18);
  const buyCostWei =
    auction && buyAmount && auction.saleClearedWei > 0n ? ceilDiv(buyAmount * auction.salePotWei * markBps, auction.saleClearedWei * 10_000n) : undefined;
  const buyCostUsd = buyCostWei !== undefined ? hypeUsd(buyCostWei) : undefined;

  async function act(kind: 'stake' | 'unstake' | 'auctionSell') {
    if (!wallet) return openConnect(true);
    if (!signer || !user) return;
    // a sale of 0 with a new reserve moves the offer already in the epoch
    const a = kind === 'auctionSell' ? (sellAmount ?? (repricing ? 0n : undefined)) : amount;
    if (a === undefined || (a === 0n && !(kind === 'auctionSell' && repricing))) return;
    const spender = kind === 'auctionSell' ? deployment.burnAuction! : deployment.staking;
    const allowance = kind === 'auctionSell' ? user.auctionAllowance : user.papurrAllowance;
    try {
      setBusy(kind);
      if (kind !== 'unstake' && allowance < a) {
        await sendTx(wallet.client, { address: globals!.papurr, abi: erc20Abi, functionName: 'approve', args: [spender, a] });
      }
      if (kind === 'auctionSell') {
        await run({ action: 'auctionSell', request: { user: wallet.address, amount: a, minTick: tick, nonce: newNonce(), deadline: deadlineIn() } }, signer);
        setSellText('');
        setReserveTick(undefined);
      } else {
        await run({ action: kind, request: { user: wallet.address, amount: a, nonce: newNonce(), deadline: deadlineIn() } }, signer);
        setText('');
      }
      toast({
        kind: 'ok',
        title: kind === 'stake' ? 'PAPURR staked' : kind === 'unstake' ? 'PAPURR unstaked' : a === 0n ? 'Minimum updated' : 'PAPURR offered',
        body:
          kind === 'auctionSell'
            ? 'Paid when the epoch ends if its price reaches your minimum; otherwise the PAPURR comes back at your next claim.'
            : undefined,
      });
      refresh();
    } catch (e) {
      toast({ kind: 'err', title: kind === 'stake' ? 'Stake failed' : kind === 'unstake' ? 'Unstake failed' : 'Sale failed', body: errorMessage(e) });
    } finally {
      setBusy(undefined);
    }
  }

  /** One claim for both sources: the staking stream, then the auction's closed epochs */
  async function claimAll() {
    if (!wallet) return openConnect(true);
    if (!signer || !user) return;
    try {
      setBusy('claim');
      const toEvm = dest === 'evm';
      if (user.pendingReward > 0n) {
        await run({ action: toEvm ? 'claimToEvm' : 'claim', request: { user: wallet.address, nonce: newNonce(), deadline: deadlineIn() } }, signer);
      }
      if (hasAuction && (user.auctionClaimable > 0n || user.auctionReturnable > 0n)) {
        await run({ action: toEvm ? 'auctionClaimToEvm' : 'auctionClaim', request: { user: wallet.address, nonce: newNonce(), deadline: deadlineIn() } }, signer);
      }
      toast({
        kind: 'ok',
        title: claimable > 0n ? 'HYPE claimed' : 'PAPURR taken back',
        body:
          claimable === 0n
            ? 'The epoch left your offer out: the PAPURR is back in your wallet.'
            : toEvm
              ? 'On its way to this wallet as native HYPE: it lands in a block or two.'
              : 'Sent to your HyperCore spot balance.',
      });
      refresh();
    } catch (e) {
      toast({ kind: 'err', title: 'Claim failed', body: errorMessage(e) });
    } finally {
      setBusy(undefined);
    }
  }

  async function buy() {
    if (!wallet) return openConnect(true);
    if (!user || !deployment.burnAuction || !buyAmount) return;
    const au = deployment.burnAuction;
    try {
      setBusy('buy');
      // the exact price now, from the contract: a buy with any other amount of HYPE attached reverts
      const [value] = (await walletReader(wallet.client).readContract({
        address: au,
        abi: burnAuctionAbi,
        functionName: 'quote',
        args: [buyAmount],
      } as never)) as readonly [bigint, bigint];
      await sendTx(wallet.client, { address: au, abi: burnAuctionAbi, functionName: 'buy', args: [buyAmount], value });
      setBuyText('');
      toast({ kind: 'ok', title: 'PAPURR bought', body: 'It is in your wallet.' });
      refresh();
    } catch (e) {
      toast({ kind: 'err', title: 'Buy failed', body: errorMessage(e) });
    } finally {
      setBusy(undefined);
    }
  }

  /** HYPE a HyperEVM payout could not push to this wallet (a contract that refused it): pulled by hand */
  async function withdrawEvm() {
    if (!wallet || !deployment.hypeBuyback) return;
    try {
      setBusy('withdrawEvm');
      await sendTx(wallet.client, { address: deployment.hypeBuyback, abi: hypeBuybackAbi, functionName: 'withdrawEvm', args: [] });
      toast({ kind: 'ok', title: 'HYPE withdrawn', body: 'It is in your wallet.' });
      refresh();
    } catch (e) {
      toast({ kind: 'err', title: 'Withdrawal failed', body: errorMessage(e) });
    } finally {
      setBusy(undefined);
    }
  }

  /** Native HYPE from the wallet into the converter's open round: sold for USDC on the trading balance */
  async function convert() {
    if (!wallet) return openConnect(true);
    if (!deployment.hypeConverter || !convertAmount || !convertOk) return;
    try {
      setBusy('convert');
      await sendTx(wallet.client, { address: deployment.hypeConverter, abi: hypeConverterAbi, functionName: 'convert', args: [], value: convertAmount });
      setConvertText('');
      toast({ kind: 'ok', title: 'HYPE converting', body: 'The USDC lands on your trading balance once the round is sold, in a minute or so.' });
      refresh();
    } catch (e) {
      toast({ kind: 'err', title: 'Conversion failed', body: errorMessage(e) });
    } finally {
      setBusy(undefined);
    }
  }

  /** HYPE a round returned that this wallet refused (a contract): pulled by hand */
  async function withdrawConverted() {
    if (!wallet || !deployment.hypeConverter) return;
    try {
      setBusy('withdrawConverted');
      await sendTx(wallet.client, { address: deployment.hypeConverter, abi: hypeConverterAbi, functionName: 'withdrawHype', args: [] });
      toast({ kind: 'ok', title: 'HYPE withdrawn', body: 'It is in your wallet.' });
      refresh();
    } catch (e) {
      toast({ kind: 'err', title: 'Withdrawal failed', body: errorMessage(e) });
    } finally {
      setBusy(undefined);
    }
  }

  const amountField = (id: string, value: string, set: (v: string) => void, maxValue: bigint, canMax: boolean) => (
    <div className="field">
      <input id={id} className="num" inputMode="decimal" autoComplete="off" placeholder="0.00" value={value} onChange={(e) => set(amountInput(e.target.value))} />
      <button type="button" className="chip" disabled={!canMax} onClick={() => set(toInput(maxValue, 18))}>
        Max
      </button>
    </div>
  );

  return (
    <div className="house-page papurr-page">
      <section className="panel house-hero">
        <div className="house-hero-main">
          <div className="house-eyebrow">PAPURR</div>
          <p className="house-intro">The token you earn by losing, and spend by staking, selling or buying back. Nothing locks.</p>
          <ul className="flow">
            <li>
              <b>1 · Lose</b>
              <span>
                Every losing close or liquidation mints PAPURR to you: {ratePerUsd !== undefined ? fmtUnits(ratePerUsd, 18, ratePerUsd < 10n * WAD ? 2 : 0) : dash} per $1
                lost right now.
              </span>
            </li>
            <li>
              <b>2 · Stake or sell</b>
              <span>
                Staked PAPURR earns the HYPE stream, bought with the stakers' share of every trade.
                {showAuction ? ' PAPURR sold to the burn auction takes a share of its pot of HYPE instead, every epoch.' : ''}
              </span>
            </li>
            {showAuction && (
              <li>
                <b>3 · Buy or burn</b>
                <span>What was sold last epoch is for sale this epoch at {markPct} over the price it got. Whatever nobody buys is burned.</span>
              </li>
            )}
          </ul>
          {phase && (
            <div className={`house-status ${phase.cls}`}>
              <i />
              <b>{phase.text}</b>
              <span>{phase.detail}</span>
            </div>
          )}
        </div>
        <div className="house-hero-figures">
          <Figure
            label="Your PAPURR"
            hint="What you hold, staked and in your wallet. Staked PAPURR earns a share of every distribution, pro rata to all staked PAPURR."
            value={user ? fmtUnits(held, 18, 0) : dash}
            sub={
              user
                ? `${fmtUnits(user.staked, 18, 0)} staked${poolShare !== undefined && user.staked > 0n ? ` (${fmtPct(poolShare)} of the pool)` : ''} · ${fmtUnits(user.papurr, 18, 0)} in wallet`
                : undefined
            }
          />
          <Figure
            label="HYPE to claim"
            hint={`HYPE earned by your stake and not claimed yet${hasAuction ? ', plus your share of every auction epoch that ended' : ''}. One claim sends it all to your HyperCore spot balance (the same address on Hyperliquid) or, if you prefer or have no HyperCore account, to this wallet on HyperEVM as native HYPE, a block or two later and less ${buyback ? fmtHype(buyback.evmFeeWei) : '0.0001'} HYPE. After 100 days without a stake, unstake or claim, anyone can send the stream's unclaimed HYPE to the protocol's dev wallet; your stake stays yours.`}
            value={user ? `${fmtHype(claimable)} HYPE` : dash}
            sub={claimSub || undefined}
            tone={sweepDaysLeft !== undefined && sweepDaysLeft <= 14 ? 'neg' : user && claimable > 0n ? 'pos' : undefined}
          >
            <button className="btn btn-primary btn-sm figure-btn" disabled={!!busy || (!!user && claimable === 0n && returnable === 0n)} onClick={() => void claimAll()}>
              {busy === 'claim'
                ? 'Claiming…'
                : !wallet
                  ? 'Connect Wallet'
                  : claimable === 0n && returnable > 0n
                    ? 'Take my PAPURR back'
                    : dest === 'evm'
                      ? 'Claim to this wallet'
                      : 'Claim HYPE'}
            </button>
            {wallet && (
              <div className="seg claim-dest" role="radiogroup" aria-label="Where the HYPE is paid">
                <button
                  role="radio"
                  aria-checked={dest === 'core'}
                  className={dest === 'core' ? 'is-on' : ''}
                  disabled={!!user && !user.coreUser}
                  title={user && !user.coreUser ? NO_CORE : 'To your HyperCore spot balance, on Hyperliquid'}
                  onClick={() => setClaimTo('core')}
                >
                  HyperCore
                </button>
                <button
                  role="radio"
                  aria-checked={dest === 'evm'}
                  className={dest === 'evm' ? 'is-on' : ''}
                  title="To this wallet on HyperEVM, as native HYPE: a block or two later, less a small fee kept for stakers"
                  onClick={() => setClaimTo('evm')}
                >
                  This wallet
                </button>
              </div>
            )}
            {evmInFlight > 0n && (
              <span className="house-figure-sub">
                {fmtHype(user!.evmQueued)} HYPE on its way to this wallet
                {user!.evmOwed > 0n && (
                  <>
                    {' · '}
                    <button className="link" disabled={!!busy} onClick={() => void withdrawEvm()}>
                      withdraw {fmtHype(user!.evmOwed)} HYPE it could not deliver
                    </button>
                  </>
                )}
              </span>
            )}
          </Figure>
        </div>
      </section>

      <div className="house-grid2">
        <Question
          title="Stake"
          lead={`Instant, no cooldown, no lockup. Stakers get ${globals ? pct(globals.rewardShares.loss) : dash} of every loss, half of the ${globals ? pct(globals.winFeeRate) : dash} win fee and a share of the house's new records above ${globals ? compactUsd(globals.rewardThreshold) : dash}, all spent on HYPE and streamed to them over ${streamDays}.`}
        >
          <div className="seg" role="tablist">
            {(['stake', 'unstake'] as const).map((m) => (
              <button
                key={m}
                role="tab"
                aria-selected={mode === m}
                className={mode === m ? 'is-on' : ''}
                onClick={() => {
                  setMode(m);
                  setText('');
                }}
              >
                {m === 'stake' ? 'Stake' : 'Unstake'}
              </button>
            ))}
          </div>
          <form
            className="stake-form"
            onSubmit={(e) => {
              e.preventDefault();
              if (amount && amount <= max && !underMin) void act(mode);
            }}
          >
            <label className="field-label" htmlFor="stake-amount">
              <span className="avail">
                {mode === 'stake' ? 'In wallet' : 'Staked'} {user ? fmtUnits(max, 18, 2) : dash}
              </span>
              PAPURR
            </label>
            {amountField('stake-amount', text, setText, max, !!user && max > 0n)}
            <button type="submit" className="btn btn-primary btn-block" disabled={!!busy || !!(user && (!amount || amount > max || underMin))}>
              {!wallet
                ? 'Connect Wallet'
                : busy === mode
                  ? mode === 'stake'
                    ? 'Staking…'
                    : 'Unstaking…'
                  : amount && amount > max
                    ? `Not enough ${mode === 'stake' ? 'PAPURR' : 'staked'}`
                    : underMin
                      ? mode === 'stake'
                        ? `Stake At Least ${fmtUnits(minStake - (user?.staked ?? 0n), 18, 0)}`
                        : `Keep ${fmtUnits(minStake, 18, 0)} Or Unstake All`
                      : mode === 'stake'
                      ? 'Stake PAPURR'
                      : 'Unstake PAPURR'}
            </button>
          </form>
          {minStake > 0n && (
            <p className="card-sub">
              Minimum stake: {fmtUnits(minStake, 18, 0)} PAPURR per account. Once there, any top-up works; unstaking
              everything always works.
            </p>
          )}
          {mode === 'unstake' && <p className="card-sub">Unstaking keeps the rewards you already earned: claim them any time.</p>}
          <Line
            label="Streaming now"
            hint={`HYPE paid out to all stakers per day right now. Each purchase spreads what is left over a new ${streamDays}.`}
            value={buyback ? `${fmtHype(buyback.perDayWei)} HYPE / day` : dash}
            sub={poolShare !== undefined && user && user.staked > 0n ? `${fmtPct(poolShare)} of it is yours` : undefined}
          />
          <Line
            label="Next buyback"
            hint={`Revenue collected since the last distribution, plus what is on its way to the buyback. The keeper sends it at least hourly; the buyback then buys HYPE ${pace}, never above the HYPE oracle price + ${buyback ? Number(buyback.slippageBps) / 100 : 0.3}%.`}
            value={globals ? `$${fmtUsd(globals.pendingDistribution + globals.buybackOwed + (buyback ? buyback.usdcWei * 10n ** 10n : 0n))}` : dash}
            sub={buyback ? `${fmtHype(buyback.boughtWei)} HYPE bought so far` : undefined}
          />
        </Question>

        {hasAuction && !showAuction && (
          <Question
            title="Burn auction: opening soon"
            lead="The burn auction is off for the first epochs after launch: until it opens, every HYPE lot the buyback books streams to stakers. Once it opens, a share of each lot buys PAPURR sold to it, what is taken is resold dearer for one epoch, and whatever nobody buys is burned."
          >
            <Line
              label="Epochs"
              hint="How long an epoch lasts once the auction runs; the timelock sets it, between 1 and 30 days."
              value={epochDays !== undefined ? `${epochDays} day${epochDays === 1 ? '' : 's'}` : dash}
            />
          </Question>
        )}

        {showAuction && (
          <Question
            title="Sell to the burn auction"
            lead={`Each epoch, a pot of HYPE (${auctionShare} of every lot the buyback books) buys PAPURR at one price. You offer PAPURR with the lowest price you accept; when the epoch ends, the cheapest offers are taken first as long as the pot pays their price, all at the same price. If yours is not taken, you get it back.`}
          >
            <Line
              label="This epoch's pot"
              hint="HYPE set aside for the running epoch so far. It grows with every lot the buyback books until the epoch ends. An epoch that takes nothing keeps its pot for the next one."
              value={auction ? `${fmtHype(auction.potWei)} HYPE` : dash}
              sub={potUsd !== undefined && auction && auction.potWei > 0n ? `≈ $${fmtUsd(potUsd)}` : undefined}
            />
            <Line
              label="Offered so far"
              hint="PAPURR offered into the running epoch, by everyone, each with its seller's minimum. The clearing below is what the epoch would pay if it closed now; the pot keeps growing until it does."
              value={auction ? `${fmtUnits(auction.soldWei, 18, 0)} PAPURR` : dash}
              sub={previewText}
            />
            <Line
              label="Epoch ends"
              hint={`Epochs last ${epochDays ?? '-'} days at least; the first sale, buy, claim or lot after that closes it and opens the next.`}
              value={endsText}
              sub={auction ? `epoch #${auction.epoch}` : undefined}
            />
            <Line
              label="Your offer"
              hint="What you offered into the running epoch and your minimum. Taken means the epoch would pay you at today's pot; left out means you would get your PAPURR back. You can move your minimum until the epoch ends."
              value={user && user.auctionSold > 0n ? `${fmtUnits(user.auctionSold, 18, 0)} PAPURR` : user ? 'none yet' : dash}
              sub={user && user.auctionSold > 0n ? `min ${tickLabel(user.auctionTick)} · ${yourTaken ? 'taken at today\'s pot' : 'left out at today\'s pot'}` : undefined}
              tone={user && user.auctionSold > 0n ? (yourTaken ? 'pos' : 'neg') : undefined}
            />
            <form
              className="stake-form"
              onSubmit={(e) => {
                e.preventDefault();
                if ((sellAmount && sellAmount <= sellMax) || (!sellAmount && repricing)) void act('auctionSell');
              }}
            >
              <label className="field-label" htmlFor="sell-amount">
                <span className="avail">In wallet {user ? fmtUnits(sellMax, 18, 2) : dash}</span>
                PAPURR to offer
              </label>
              {amountField('sell-amount', sellText, setSellText, sellMax, !!user && sellMax > 0n)}
              <label className="field-label" htmlFor="sell-min">
                <span className="avail">{tickLabel(tick)}</span>
                Lowest price you accept
                <Hint text="A floor on what the epoch pays you. The close takes offers from the cheapest up while the pot covers their price, and pays them all the same price, never below their floor. Set it too high and you are left out (and keep your PAPURR); at any price, whoever offers after you can lower what you get, down to almost nothing." />
              </label>
              <input
                id="sell-min"
                type="range"
                className="lev-slider reserve-slider"
                min={0}
                max={TICKS - 1}
                step={1}
                value={tick}
                disabled={!!busy || !auction}
                onChange={(e) => setReserveTick(Number(e.target.value))}
                aria-valuetext={tickLabel(tick)}
              />
              <div className="slider-legend">
                <span>Any price</span>
                {refPrice !== undefined && (
                  <button type="button" className="link" onClick={() => setReserveTick(tickAtOrBelow((refPrice * 4n) / 5n))}>
                    suggested: a fifth under {auction && auction.lastClearedWei > 0n ? 'the last price paid' : "today's price"}
                  </button>
                )}
                <span>Higher</span>
              </div>
              {auction && user && (sellAmount || user.auctionSold > 0n) && (
                <p className={`reserve-verdict ${tick === 0 ? 'warn' : simTaken ? 'pos' : 'neg'}`}>
                  {tick === 0
                    ? 'Any price: taken whatever the pot pays, and anyone offering after you lowers it.'
                    : sim && simTaken
                      ? `At today's pot you would be taken, at ${perK(sim.price)}.`
                      : sim && sim.price > 0n
                        ? `Above today's clearing (${perK(sim.price)}): left out as things stand, you would keep your PAPURR. The pot may still grow.`
                        : "Nothing would be taken at today's pot; it may still grow before the epoch ends."}
                </p>
              )}
              <button
                type="submit"
                className="btn btn-primary btn-block"
                disabled={!!busy || !!(user && ((!sellAmount && !repricing) || (sellAmount !== undefined && sellAmount > sellMax)))}
              >
                {!wallet
                  ? 'Connect Wallet'
                  : busy === 'auctionSell'
                    ? sellAmount
                      ? 'Offering…'
                      : 'Updating…'
                    : sellAmount && sellAmount > sellMax
                      ? 'Not enough PAPURR'
                      : !sellAmount && repricing
                        ? 'Update my minimum'
                        : user && user.auctionSold > 0n
                          ? 'Add to my offer'
                          : 'Offer PAPURR'}
              </button>
            </form>
            <p className="card-sub">
              Paid when the epoch ends, at the price it clears at, under "HYPE to claim". Left out: your PAPURR comes back at your next claim. An
              offer cannot be withdrawn before the epoch ends, only re-priced.
            </p>
          </Question>
        )}

        {showAuction && (
          <Question
            title="Buy PAPURR"
            lead={`What was sold last epoch is for sale this epoch, at the price that epoch paid plus ${markPct}. What you pay streams to stakers, but for the ${feePct} that goes to the protocol's dev wallet. Whatever nobody buys is burned when this epoch ends.`}
          >
            <Line
              label="On sale"
              hint="The previous epoch's PAPURR that nobody bought yet. Paid in HYPE from your wallet, not through a session key."
              value={auction ? `${fmtUnits(buyMax, 18, 0)} PAPURR` : dash}
              sub={
                salePerK !== undefined && buyMax > 0n
                  ? `≈ $${fmtUsd(salePerK)} per 1,000 PAPURR, from epoch #${auction!.saleEpoch}`
                  : auction
                    ? 'nothing until an epoch with sales and a pot ends'
                    : undefined
              }
            />
            <Line
              label="Last epoch paid"
              hint="The previous epoch's pot over the PAPURR its close took: the price before the mark-up. Offers above it were left out and went back to their sellers."
              value={auction && auction.epoch > 0n ? `${fmtHype(auction.lastPotWei)} HYPE for ${fmtUnits(auction.lastClearedWei, 18, 0)} PAPURR` : dash}
              sub={
                lastPerK !== undefined
                  ? `≈ $${fmtUsd(lastPerK)} per 1,000 PAPURR · ${fmtUnits(auction!.lastResoldWei, 18, 0)} bought back so far${auction!.lastSoldWei > auction!.lastClearedWei ? ` · ${fmtUnits(auction!.lastSoldWei - auction!.lastClearedWei, 18, 0)} left out` : ''}`
                  : auction && auction.epoch > 0n && auction.lastSoldWei === 0n
                    ? 'nobody offered: the pot carried over'
                    : auction && auction.epoch > 0n
                      ? 'nothing taken: every minimum was above what the pot paid, it carried over'
                      : undefined
              }
            />
            <Line
              label="Unsold burns"
              hint="When the running epoch ends, what is left of the previous epoch's PAPURR is burned for good."
              value={buyMax > 0n ? endsText : dash}
            />
            <form
              className="stake-form"
              onSubmit={(e) => {
                e.preventDefault();
                if (buyAmount && buyAmount <= buyMax) void buy();
              }}
            >
              <label className="field-label" htmlFor="buy-amount">
                <span className="avail">Costs {buyCostWei !== undefined ? `${fmtHype(buyCostWei)} HYPE${buyCostUsd !== undefined ? ` ≈ $${fmtUsd(buyCostUsd)}` : ''}` : dash}</span>
                PAPURR to buy
              </label>
              {amountField('buy-amount', buyText, setBuyText, buyMax, buyMax > 0n)}
              <button type="submit" className="btn btn-primary btn-block" disabled={!!busy || buyMax === 0n || !buyAmount || buyAmount > buyMax}>
                {!wallet ? 'Connect Wallet' : busy === 'buy' ? 'Buying…' : buyMax === 0n ? 'Nothing on sale' : buyAmount && buyAmount > buyMax ? 'Not that much on sale' : 'Buy PAPURR'}
              </button>
            </form>
          </Question>
        )}

        {hasConverter && (
          <Question
            title="Convert HYPE to USDC"
            lead={`Put HYPE from this wallet (claimed rewards, or any) back to work as USDC on your trading balance. Contributions pool into rounds that close every ${converter ? converter.closeInterval : 30} s and sell on the Hyperliquid book at the HYPE oracle price less ${converter ? converter.slippageBps / 100 : 0.3}% at most; what does not sell comes back as HYPE.`}
          >
            <Line
              label="Price now"
              hint="The lowest price the next order asks: the HYPE oracle price less the slippage bound. The order fills at the book's best bids, so the round gets at least this per HYPE."
              value={converter && converter.askPx > 0n ? `$${fmtUnits(converter.askPx, 8, 3)} per HYPE` : dash}
            />
            <Line
              label="Converting now"
              hint="HYPE pooled in the open round, and in the round being sold. One round at a time; each takes a few blocks from closing to the credit."
              value={converter ? `${fmtHype(converter.openWei + converter.activeWei)} HYPE` : dash}
              sub={converter ? (converter.halted ? 'halted by the guardian' : `round #${converter.openRound}${converter.activeRound > 0n ? `, round #${converter.activeRound} selling` : ''}`) : undefined}
            />
            <Line
              label="Yours in flight"
              hint="Your HYPE in rounds not credited yet. It lands on your trading balance as USDC once its round is sold."
              value={user ? `${fmtHype(user.convertPending)} HYPE` : dash}
              sub={user && user.convertOwed > 0n ? `${fmtHype(user.convertOwed)} HYPE returned, to withdraw` : undefined}
            />
            {user && user.convertOwed > 0n && (
              <button className="btn btn-ghost btn-block" disabled={!!busy} onClick={() => void withdrawConverted()}>
                {busy === 'withdrawConverted' ? 'Withdrawing…' : `Withdraw ${fmtHype(user.convertOwed)} HYPE`}
              </button>
            )}
            <form
              className="stake-form"
              onSubmit={(e) => {
                e.preventDefault();
                if (convertOk) void convert();
              }}
            >
              <label className="field-label" htmlFor="convert-amount">
                <span className="avail">In wallet {user ? fmtUnits(user.gas, 18, 4) : dash}</span>
                HYPE to convert
              </label>
              {amountField('convert-amount', convertText, setConvertText, convertMax, !!user && convertMax > 0n)}
              <button type="submit" className="btn btn-primary btn-block" disabled={!!busy || !!(user && !convertOk)}>
                {!wallet
                  ? 'Connect Wallet'
                  : busy === 'convert'
                    ? 'Converting…'
                    : convertAmount && convertAmount > convertMax
                      ? 'Not enough HYPE (gas kept)'
                      : convertAmount && convertAmount < convertMin
                        ? `At least ${fmtUnits(convertMin, 18, 2)} HYPE`
                        : convertAmount && convertAmount % HYPE_LOT !== 0n
                          ? 'Whole hundredths of a HYPE'
                          : 'Convert to USDC'}
              </button>
            </form>
            <p className="card-sub">
              {convertEst !== undefined ? `≈ $${fmtUsd(convertEst)} at today's floor price, credited to your trading balance.` : `Whole hundredths of a HYPE, at least ${fmtUnits(convertMin, 18, 2)}. A wallet transaction with HYPE attached.`}
            </p>
          </Question>
        )}

        <Question title="Where PAPURR comes from" lead="Losing. Every losing close mints PAPURR to you on what the house keeps, and a liquidation on your whole margin. Wins mint nothing.">
          <div className="big-rate">
            <b className="num">{ratePerUsd !== undefined ? fmtUnits(ratePerUsd, 18, ratePerUsd < 10n * WAD ? 2 : 0) : dash}</b>
            <span>PAPURR per $1 you lose, right now</span>
          </div>
          {globals && globals.mint.fixedRate > 0n ? (
            <>
              <Line
                label="Soft launch"
                hint="During the soft launch every dollar lost mints the same amount, whatever the house holds. The timelock later switches to the emission curve below; the switch is announced with its delay."
                value={`${fmtUnits(globals.mint.fixedRate, 18, 2)} per $1, fixed`}
                sub="until the timelock switches to the curve"
              />
              <Line
                label="The curve, once switched on"
                hint="It starts at the flat rate and falls as more is minted past the threshold, never coming back up; under the threshold it is boosted, the lower the house the more, never above the flat rate."
                value={`${fmtUnits(globals.mint.flatRate, 18, 0)} per $1 at first, under ${compactUsd(globals.mint.flatThreshold)} of house`}
                sub={`then a quarter of it after ${compactUsd(globals.mint.tailK)} lost past the threshold`}
              />
            </>
          ) : (
            <>
              <Line
                label="Rate now"
                hint="Under the threshold the curve's rate is boosted: the lower the house, the more, up to the multiple at an empty house, and never above the flat rate. Past the threshold, the rate falls as more is minted, and never comes back up."
                value={globals && ratePerUsd !== undefined ? `${fmtUnits(ratePerUsd, 18, 2)} per $1` : dash}
                sub={
                  globals && flatLeft !== undefined
                    ? flatLeft > 0n
                      ? globals.mint.lowHouseMultiple === undefined
                        ? `the flat rate until the house reaches ${compactUsd(globals.mint.flatThreshold)}, ${compactUsd(flatLeft)} away`
                        : `the curve's rate ${boostNow(globals)}, the house being ${compactUsd(flatLeft)} under ${compactUsd(globals.mint.flatThreshold)} (up to ×${fmtUnits(globals.mint.lowHouseMultiple, 18, 1)} when empty), at most ${fmtUnits(globals.mint.flatRate, 18, 0)}`
                      : `the house is past ${compactUsd(globals.mint.flatThreshold)}: the rate decays`
                    : undefined
                }
              />
              <Line
                label="Later"
                hint="Past the threshold, the rate is the flat rate times (K / (K + T))², T being the dollars already minted on that curve."
                value={globals ? `a quarter after ${compactUsd(globals.mint.tailK)} lost past the threshold` : dash}
              />
            </>
          )}
          <p className="card-sub">The trade form shows how much PAPURR a liquidation would mint before you open.</p>
        </Question>

        <Question title="The token" lead="No pre-mint, no team or investor allocation, no airdrop: every PAPURR was minted to a trader on a loss.">
          <Line label="Supply" value={globals ? fmtUnits(globals.papurrSupply, 18, 0) : dash} />
          <Line
            label="Staked"
            value={globals ? fmtUnits(globals.totalStaked, 18, 0) : dash}
            sub={ratio !== undefined ? `${fmtPct(ratio)} of the supply` : undefined}
          />
          {hasAuction && (
            <Line
              label="Burned"
              hint="PAPURR sold to the auction that nobody bought back, burned for good when the next epoch closed."
              value={auction ? fmtUnits(auction.totalBurnedWei, 18, 0) : dash}
              sub={auction ? `of ${fmtUnits(auction.totalSoldWei, 18, 0)} sold, ${fmtUnits(auction.totalResoldWei, 18, 0)} bought back` : undefined}
            />
          )}
          <div className="house-line">
            <span className="house-line-label">
              Token <Hint text="PAPURR, an ERC-20 on HyperEVM. Click to copy the address." />
            </span>
            <CopyAddr addr={globals?.papurr} />
          </div>
          <div className="house-line">
            <span className="house-line-label">Staking contract</span>
            <CopyAddr addr={deployment.staking} />
          </div>
          {deployment.hypeBuyback && (
            <div className="house-line">
              <span className="house-line-label">
                HYPE buyback <Hint text="Holds the USDC to spend and the HYPE bought, on its own HyperCore account." />
              </span>
              <CopyAddr addr={deployment.hypeBuyback} />
            </div>
          )}
          {hasAuction && (
            <div className="house-line">
              <span className="house-line-label">
                Burn auction <Hint text="Holds the PAPURR on sale; its HYPE stays on the buyback's account." />
              </span>
              <CopyAddr addr={deployment.burnAuction} />
            </div>
          )}
          <a className="house-more" href="#/docs/papurr/overview">
            How PAPURR works →
          </a>
        </Question>
      </div>
    </div>
  );
}

function CopyAddr({ addr }: { addr?: string }) {
  const { toast } = useApp();
  if (!addr) return <span>{dash}</span>;
  return (
    <button
      className="link num"
      title={addr}
      onClick={() => copyText(addr).then((ok) => toast({ kind: 'info', title: ok ? 'Address copied' : 'Copy it by hand: no clipboard here' }))}
    >
      {shortAddr(addr)} ⧉
    </button>
  );
}
